import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";

import { cn } from "@/shared/lib";

import { useAssetLookup } from "../../api/useAssetLookup";
import { formatGameTime } from "../../format/formatGameTime";
import type { AnalysisDeath } from "../../model/Analysis";
import { AssetIcon } from "../AssetIcon";

const TITLE = msg({ id: "analysis.deaths.timeline", message: "Deaths on the match timeline" });
const MARK = msg({ id: "analysis.deaths.mark", message: "{time} — {killer}" });
const NOT_HERO = msg({ id: "analysis.deaths.mark_not_hero", message: "creeps or towers" });
const LANE = msg({ id: "analysis.deaths.timeline_lane", message: "lane" });

// Подписи шкалы: каждые 10 минут; ближе 8% к концу не ставим — налезут на длительность матча.
const TICK_S = 10 * 60;
const MIN_GAP_TO_END = 0.08;

type DeathTimelineProps = {
  deaths: readonly AnalysisDeath[];
  durationS: number;
  // Отмечать красным только смерти в невыгодных драках (подробности с обстановкой смертей);
  // иначе — все смерти красные, как у разборов без неё.
  markFights?: boolean;
  // Конец фазы линий, секунды: участок шкалы до него подписан «линия». Нет — без подписи.
  laneEndS?: number | undefined;
};

// Смерти на шкале матча: иконка убийцы в момент смерти, подписи времени снизу.
export const DeathTimeline = ({
  deaths,
  durationS,
  markFights = false,
  laneEndS,
}: DeathTimelineProps) => {
  const { _ } = useLingui();
  const findHero = useAssetLookup("heroes");
  const duration = Math.max(durationS, 1);
  const percent = (seconds: number): string =>
    `${Math.min(100, (seconds / duration) * 100).toFixed(1)}%`;
  const ticks = Array.from(
    { length: Math.floor(duration / TICK_S) + 1 },
    (_tick, i) => i * TICK_S,
  ).filter((seconds) => seconds === 0 || seconds / duration < 1 - MIN_GAP_TO_END);
  return (
    <figure className="m-0 flex flex-col gap-1.5">
      <figcaption className="text-[13px] text-subtle">{_(TITLE)}</figcaption>
      <ol className="relative h-12 rounded-lg bg-surface-raised">
        {laneEndS !== undefined && (
          <li
            aria-hidden="true"
            className="absolute inset-y-0 left-0 rounded-l-lg border-r border-dashed border-border bg-surface"
            style={{ width: percent(laneEndS) }}
          >
            <span className="absolute top-0.5 left-2 text-[11px] text-subtle">{_(LANE)}</span>
          </li>
        )}
        {deaths.map((death, index) => {
          const killer = death.killer_hero_id === null ? null : findHero(death.killer_hero_id);
          const ring = !markFights || death.fight !== null ? "border-bad" : "border-border-strong";
          const label = _({
            ...MARK,
            values: { time: formatGameTime(death.at_s), killer: killer?.name ?? _(NOT_HERO) },
          });
          return (
            <li
              // Смерти отсортированы по времени и не меняются — индекс делает ключ уникальным.
              key={`${death.at_s}-${index}`}
              title={label}
              data-death-at={death.at_s}
              className="absolute top-[7px] size-[34px] -translate-x-1/2"
              // Иконка по центру момента, но не за краем шкалы: смерть в последние секунды
              // матча на узком экране иначе уезжает за рамку.
              style={{ left: `clamp(17px, ${percent(death.at_s)}, calc(100% - 17px))` }}
            >
              <span className="sr-only">{label}</span>
              {killer === null ? (
                <span
                  aria-hidden="true"
                  className={cn(
                    "inline-flex size-[34px] items-center justify-center rounded-full border-2 bg-surface text-bad",
                    ring,
                  )}
                >
                  ×
                </span>
              ) : (
                <AssetIcon
                  asset={killer}
                  size={34}
                  decorative
                  className={cn("rounded-full border-2", ring)}
                />
              )}
            </li>
          );
        })}
      </ol>
      <div aria-hidden="true" className="relative h-4 font-mono text-[11px] text-subtle">
        {ticks.map((seconds) => (
          <span
            key={seconds}
            className={
              seconds === 0 ? "absolute left-0" : "absolute -translate-x-1/2 whitespace-nowrap"
            }
            style={seconds === 0 ? undefined : { left: percent(seconds) }}
          >
            {formatGameTime(seconds)}
          </span>
        ))}
        <span className="absolute right-0">{formatGameTime(durationS)}</span>
      </div>
    </figure>
  );
};
